import { Button } from '@liza/ui/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@liza/ui/components/ui/empty';
import { cn } from '@liza/ui/utils/cn';
import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Props = {
  error?: unknown;
  /** Where "go home" lands. Defaults to the app root. */
  homeHref?: string;
  /** Show the raw error message in a collapsed disclosure. Internal apps only. */
  showDetails?: boolean;
  className?: string;
};

/**
 * The fallback a router renders when a route fails to load or render: translated, styled, and with
 * two ways out. Wire it up as the router's `defaultErrorComponent`.
 *
 * Both actions are full-document navigations rather than an in-place `reset`. The common cause is a
 * route chunk that no longer exists, and re-rendering can't recover that — the failed module stays
 * failed in the browser's module map, so only a document load (which re-fetches `index.html` and its
 * current asset hashes) gets the user moving again.
 *
 * Grows to fill a flex container (a route outlet, typically) with a `60svh` floor for the outlets
 * that don't stretch, rather than locking to the viewport — so it reads correctly both as a full page
 * and inside a panel or drawer.
 */
export const RouteErrorCard = ({
  error,
  homeHref = '/',
  showDetails = false,
  className,
}: Props): React.ReactElement => {
  const { t } = useTranslation('common');
  const detail = showDetails && error instanceof Error ? error.message : undefined;

  return (
    <Empty className={cn('min-h-[60svh] justify-center', className)} role="alert" data-testid="route-error-card">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <TriangleAlert className="text-destructive" />
        </EmptyMedia>
        <EmptyTitle>{t('routeError.title')}</EmptyTitle>
        <EmptyDescription>{t('routeError.description')}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => window.location.reload()} data-testid="route-error-retry">
            {t('routeError.tryAgain')}
          </Button>
          <Button variant="outline" onClick={() => window.location.assign(homeHref)} data-testid="route-error-home">
            {t('routeError.goHome')}
          </Button>
        </div>
        {detail !== undefined && (
          <details className="w-full text-left" data-testid="route-error-details">
            <summary className="cursor-pointer text-xs text-muted-foreground">{t('routeError.details')}</summary>
            <p className="mt-2 font-mono text-xs wrap-break-word text-muted-foreground">{detail}</p>
          </details>
        )}
      </EmptyContent>
    </Empty>
  );
};
